import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');
    setStatus('sending');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg?.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '送信に失敗しました。時間を置いて再度お試しください。');
      }
    } catch {
      setStatus('error');
      setErrorMsg('送信に失敗しました。時間を置いて再度お試しください。');
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <section className="pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100 px-4 py-1.5 rounded-full mb-6">
                Contact
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-6">
                お問い合わせ
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                Web制作、デザイン、動画、事務作業の代行...どんな小さな悩みでも大丈夫。仙台のパートナーとして、一緒に解決策を探します。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Contact Info Sidebar */}
              <div className="lg:col-span-2">
                <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200">
                  <h3 className="font-heading font-bold text-lg text-foreground-950 mb-4">
                    株式会社ヴェリテ
                  </h3>
                  <div className="space-y-4 text-sm">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-phone-line text-accent-600" />
                      </div>
                      <div>
                        <p className="text-foreground-600 text-xs mb-0.5">TEL</p>
                        <p className="text-foreground-900 font-semibold">022-211-5488</p>
                        <p className="text-foreground-500 text-xs mt-0.5">平日 9:00〜18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-mail-line text-accent-600" />
                      </div>
                      <div>
                        <p className="text-foreground-600 text-xs mb-0.5">Mail</p>
                        <p className="text-foreground-900 font-semibold break-all">verite@frise.support</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-line text-accent-600" />
                      </div>
                      <div>
                        <p className="text-foreground-600 text-xs mb-0.5">所在地</p>
                        <p className="text-foreground-900">〒980-0802</p>
                        <p className="text-foreground-900">宮城県仙台市青葉区二日町14-14</p>
                        <p className="text-foreground-900">二日町ゲートビル2階</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-time-line text-accent-600" />
                      </div>
                      <div>
                        <p className="text-foreground-600 text-xs mb-0.5">返信目安</p>
                        <p className="text-foreground-900">通常2営業日以内</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-3">
                <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200">
                  <h3 className="font-heading font-bold text-xl text-foreground-950 mb-6">
                    お問い合わせフォーム
                  </h3>

                  {status === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-3xl text-accent-600" />
                      </div>
                      <p className="text-foreground-700 font-medium">お問い合わせありがとうございます。通常2営業日以内にご返信いたします。</p>
                    </div>
                  ) : (
                    <form
                      action="https://readdy.ai/api/form/d93p0dtmi650so75dt1g"
                      method="POST"
                      data-readdy-form="d93p0dtmi650so75dt1g"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-2">
                            お名前
                          </label>
                          <input
                            type="text"
                            name="name"
                            placeholder="山田 太郎"
                            required
                            className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-2">
                            会社名
                          </label>
                          <input
                            type="text"
                            name="company"
                            placeholder="株式会社〇〇"
                            className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-2">
                            メールアドレス
                          </label>
                          <input
                            type="email"
                            name="email"
                            placeholder="your@email.com"
                            required
                            className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-2">
                            電話番号
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            placeholder="022-000-0000"
                            className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-2">
                          ご希望のサービス
                        </label>
                        <select
                          name="service"
                          className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                        >
                          <option value="">選択してください</option>
                          <option value="web">Web制作</option>
                          <option value="design">デザイン・印刷</option>
                          <option value="video">動画制作</option>
                          <option value="bpo">BPO・事務サポート</option>
                          <option value="other">その他・相談</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-2">
                          お問い合わせ内容
                        </label>
                        <textarea
                          name="message"
                          placeholder="お困りのことやご希望をお聞かせください..."
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-3 rounded-lg border border-background-400 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none transition-shadow"
                        />
                        <p className="text-xs text-foreground-500 mt-1 text-right">500文字以内</p>
                      </div>

                      <div className="hp-field">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      {status === 'error' && (
                        <div className="p-4 bg-primary-100 rounded-lg">
                          <p className="text-sm text-primary-700">{errorMsg}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'sending'}
                        className="w-full py-4 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                      >
                        {status === 'sending' ? '送信中...' : '送信する'}
                      </button>

                      <p className="text-center text-xs text-foreground-500">
                        送信により、<Link to="/privacy" className="text-primary-600 underline hover:text-primary-700 cursor-pointer">プライバシーポリシー</Link>に同意したものとみなします。
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}